'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FitnessAndHealthCalculator() {
  const t = useTranslations("tools.fitnessAndHealthCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentCalculator, setCurrentCalculator] = useState<string | null>(null);
  const [currentUnit, setCurrentUnit] = useState('metric');

  // BMI State
  const [bmiInputs, setBmiInputs] = useState({ weight: '', height: '' });
  const [bmiResults, setBmiResults] = useState<any>(null);

  // BMR State
  const [bmrInputs, setBmrInputs] = useState({ age: '', gender: '', weight: '', height: '', activity: '' });
  const [bmrResults, setBmrResults] = useState<any>(null);

  // Body Fat State
  const [bfInputs, setBfInputs] = useState({ age: '', gender: '', height: '', neck: '', waist: '', hip: '' });
  const [bfResults, setBfResults] = useState<any>(null);

  // Calorie State
  const [calInputs, setCalInputs] = useState({ age: '', gender: '', weight: '', height: '', activity: '', goal: '' });
  const [calResults, setCalResults] = useState<any>(null);

  // Ideal Weight State
  const [iwInputs, setIwInputs] = useState({ gender: '', height: '' });
  const [iwResults, setIwResults] = useState<any>(null);

  // Heart Rate State
  const [hrInputs, setHrInputs] = useState({ age: '', resting: '' });
  const [hrResults, setHrResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '600px' },
      { maxWidth: '800px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorGrid: {
      display: 'grid',
      gap: '1.5rem',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      marginTop: '2rem'
    },
    calculatorCard: {
      background: 'white',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '2px solid transparent'
    },
    cardIcon: {
      fontSize: '2.5rem',
      marginBottom: '1rem',
      display: 'block'
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem'
    },
    cardDescription: {
      fontSize: '0.9rem',
      color: colors.textSecondary,
      lineHeight: 1.5
    },
    formContainer: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    formHeader: {
      marginBottom: '1.5rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #e2e8f0'
    },
    unitToggle: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem'
    },
    unitBtn: getResponsiveStyle(
      {
        flex: 1,
        border: '2px solid #e2e8f0',
        background: 'white',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontWeight: 600,
        color: colors.textSecondary
      },
      { padding: '0.5rem 1rem', fontSize: '0.9rem' },
      { padding: '0.625rem 1.25rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1.125rem' }
    ),
    unitBtnActive: {
      background: '#2563eb',
      color: 'white',
      borderColor: '#2563eb'
    },
    formRow: {
      display: 'grid',
      gap: '1.25rem',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      marginBottom: '1.25rem'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const
    },
    label: {
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
      fontSize: '0.95rem'
    },
    input: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.2s',
        background: 'white',
        width: '100%'
      },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
      { padding: '1rem 1.5rem', fontSize: '1.125rem' }
    ),
    select: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.2s',
        background: 'white',
        width: '100%'
      },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
      { padding: '1rem 1.5rem', fontSize: '1.125rem' }
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        fontWeight: 700,
        color: '#fff',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        border: 'none',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s',
        marginTop: '1rem',
        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
    ),
    backBtn: getResponsiveStyle(
      {
        background: '#64748b',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        marginBottom: '1rem',
        fontWeight: 600,
        transition: 'all 0.2s'
      },
      { padding: '0.5rem 1rem', fontSize: '0.9rem' },
      { padding: '0.625rem 1.25rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1.125rem' }
    ),
    results: getResponsiveStyle(
      {
        marginTop: '2rem',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '1rem',
        borderLeft: '4px solid #22c55e'
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    resultsTitle: {
      color: '#166534',
      marginBottom: '1rem',
      fontSize: '1.3rem',
      fontWeight: 600
    },
    resultGrid: {
      display: 'grid',
      gap: '1rem',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))'
    },
    resultItem: {
      background: 'white',
      padding: '1rem',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginBottom: '0.25rem',
      fontWeight: 500
    },
    resultValue: {
      display: 'block',
      fontSize: '1.5rem',
      fontWeight: 700,
      color: colors.textPrimary
    },
    resultValueHighlight: {
      color: '#2563eb',
      fontSize: '1.75rem'
    },
    interpretation: {
      marginTop: '1rem',
      padding: '1rem',
      background: 'white',
      borderRadius: '0.5rem',
      borderLeft: '3px solid #f59e0b'
    },
    interpretationTitle: {
      fontWeight: 600,
      color: '#92400e',
      marginBottom: '0.5rem'
    },
    interpretationText: {
      color: colors.textSecondary,
      lineHeight: 1.6
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' }
    ),
    reference: {
      marginBottom: '0.75rem'
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.2s'
    }
  };

  const calculators = [
    { id: 'bmi', icon: '📊', title: t('bmiTitle'), description: t('bmiDescription') },
    { id: 'bmr', icon: '🔥', title: t('bmrTitle'), description: t('bmrDescription') },
    { id: 'bodyfat', icon: '📏', title: t('bodyFatTitle'), description: t('bodyFatDescription') },
    { id: 'calorie', icon: '🍎', title: t('calorieTitle'), description: t('calorieDescription') },
    { id: 'idealweight', icon: '⚖️', title: t('idealWeightTitle'), description: t('idealWeightDescription') },
    { id: 'heartrate', icon: '❤️', title: t('heartRateTitle'), description: t('heartRateDescription') }
  ];

  const handleCalculatorSelect = (calcId: string) => {
    setCurrentCalculator(calcId);
    setBmiResults(null);
    setBmrResults(null);
    setBfResults(null);
    setCalResults(null);
    setIwResults(null);
    setHrResults(null);
  };

  const handleBmiCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    let weight = parseFloat(bmiInputs.weight);
    let height = parseFloat(bmiInputs.height);

    if (currentUnit === 'imperial') {
      weight = weight * 0.453592;
      height = height * 2.54;
    }

    const heightM = height / 100;
    const bmi = weight / (heightM * heightM);

    let category = '';
    let interpretation = '';

    if (bmi < 18.5) {
      category = t('bmiUnderweight');
      interpretation = t('bmiUnderweightInterpretation');
    } else if (bmi < 25) {
      category = t('bmiNormal');
      interpretation = t('bmiNormalInterpretation');
    } else if (bmi < 30) {
      category = t('bmiOverweight');
      interpretation = t('bmiOverweightInterpretation');
    } else {
      category = t('bmiObese');
      interpretation = t('bmiObeseInterpretation');
    }

    setBmiResults({ bmi: bmi.toFixed(1), category, interpretation });
  };

  const handleBmrCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const age = parseInt(bmrInputs.age);
    const gender = bmrInputs.gender;
    let weight = parseFloat(bmrInputs.weight);
    let height = parseFloat(bmrInputs.height);
    const activity = parseFloat(bmrInputs.activity);

    if (currentUnit === 'imperial') {
      weight = weight * 0.453592;
      height = height * 2.54;
    }

    let bmr;
    if (gender === 'male') {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }

    const tdee = bmr * activity;
    const weightLoss = tdee - 500;
    const weightGain = tdee + 500;

    setBmrResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      weightLoss: Math.round(weightLoss),
      weightGain: Math.round(weightGain)
    });
  };

  const handleBodyFatCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const gender = bfInputs.gender;
    let height = parseFloat(bfInputs.height);
    let neck = parseFloat(bfInputs.neck);
    let waist = parseFloat(bfInputs.waist);
    let hip = gender === 'female' ? parseFloat(bfInputs.hip) : 0;

    if (currentUnit === 'imperial') {
      height = height * 2.54;
      neck = neck * 2.54;
      waist = waist * 2.54;
      hip = hip * 2.54;
    }

    let bodyFat;
    if (gender === 'male') {
      bodyFat = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height)) - 450;
    } else {
      bodyFat = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height)) - 450;
    }

    let category = '';
    let interpretation = '';

    if (gender === 'male') {
      if (bodyFat < 6) {
        category = t('bfEssential');
        interpretation = t('bfEssentialInterpretation');
      } else if (bodyFat < 14) {
        category = t('bfAthletes');
        interpretation = t('bfAthletesInterpretation');
      } else if (bodyFat < 18) {
        category = t('bfFitness');
        interpretation = t('bfFitnessInterpretation');
      } else if (bodyFat < 25) {
        category = t('bfAverage');
        interpretation = t('bfAverageInterpretation');
      } else {
        category = t('bfObese');
        interpretation = t('bfObeseInterpretation');
      }
    } else {
      if (bodyFat < 14) {
        category = t('bfEssential');
        interpretation = t('bfEssentialInterpretation');
      } else if (bodyFat < 21) {
        category = t('bfAthletes');
        interpretation = t('bfAthletesInterpretation');
      } else if (bodyFat < 25) {
        category = t('bfFitness');
        interpretation = t('bfFitnessInterpretation');
      } else if (bodyFat < 32) {
        category = t('bfAverage');
        interpretation = t('bfAverageInterpretation');
      } else {
        category = t('bfObese');
        interpretation = t('bfObeseInterpretation');
      }
    }

    setBfResults({ bodyFat: bodyFat.toFixed(1), category, interpretation });
  };

  const handleCalorieCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const age = parseInt(calInputs.age);
    const gender = calInputs.gender;
    let weight = parseFloat(calInputs.weight);
    let height = parseFloat(calInputs.height);
    const activity = parseFloat(calInputs.activity);
    const goal = calInputs.goal;

    if (currentUnit === 'imperial') {
      weight = weight * 0.453592;
      height = height * 2.54;
    }

    let bmr;
    if (gender === 'male') {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }

    const tdee = bmr * activity;
    const maintain = Math.round(tdee);
    const loss = Math.round(tdee - 500);
    const gain = Math.round(tdee + 500);

    let interpretation = '';
    if (goal === 'lose') {
      interpretation = t('calLoseInterpretation', { calories: loss });
    } else if (goal === 'maintain') {
      interpretation = t('calMaintainInterpretation', { calories: maintain });
    } else {
      interpretation = t('calGainInterpretation', { calories: gain });
    }

    setCalResults({ maintain, loss, gain, interpretation });
  };

  const handleIdealWeightCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const gender = iwInputs.gender;
    let height = parseFloat(iwInputs.height);

    if (currentUnit === 'imperial') {
      height = height * 2.54;
    }

    const heightInches = height / 2.54;
    let robinson, miller, devine, hamwi;

    if (gender === 'male') {
      robinson = 52 + 1.9 * (heightInches - 60);
      miller = 56.2 + 1.41 * (heightInches - 60);
      devine = 50 + 2.3 * (heightInches - 60);
      hamwi = 48 + 2.7 * (heightInches - 60);
    } else {
      robinson = 49 + 1.7 * (heightInches - 60);
      miller = 53.1 + 1.36 * (heightInches - 60);
      devine = 45.5 + 2.3 * (heightInches - 60);
      hamwi = 45.5 + 2.2 * (heightInches - 60);
    }

    const convert = currentUnit === 'imperial' ? 2.20462 : 1;
    const avg = ((robinson + miller + devine + hamwi) / 4 * convert).toFixed(1);
    const min = (Math.min(robinson, miller, devine, hamwi) * convert).toFixed(1);
    const max = (Math.max(robinson, miller, devine, hamwi) * convert).toFixed(1);

    setIwResults({
      robinson: (robinson * convert).toFixed(1),
      miller: (miller * convert).toFixed(1),
      devine: (devine * convert).toFixed(1),
      hamwi: (hamwi * convert).toFixed(1),
      avg,
      min,
      max
    });
  };

  const handleHeartRateCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const age = parseInt(hrInputs.age);
    const resting = hrInputs.resting ? parseInt(hrInputs.resting) : null;

    const maxHR = 220 - age;

    let fatBurnMin, fatBurnMax, cardioMin, cardioMax, peakMin, peakMax;

    if (resting) {
      const reserve = maxHR - resting;
      fatBurnMin = Math.round(resting + reserve * 0.5);
      fatBurnMax = Math.round(resting + reserve * 0.7);
      cardioMin = Math.round(resting + reserve * 0.7);
      cardioMax = Math.round(resting + reserve * 0.85);
      peakMin = Math.round(resting + reserve * 0.85);
      peakMax = Math.round(resting + reserve * 0.95);
    } else {
      fatBurnMin = Math.round(maxHR * 0.5);
      fatBurnMax = Math.round(maxHR * 0.7);
      cardioMin = Math.round(maxHR * 0.7);
      cardioMax = Math.round(maxHR * 0.85);
      peakMin = Math.round(maxHR * 0.85);
      peakMax = Math.round(maxHR * 0.95);
    }

    setHrResults({
      maxHR,
      fatBurn: `${fatBurnMin}-${fatBurnMax}`,
      cardio: `${cardioMin}-${cardioMax}`,
      peak: `${peakMin}-${peakMax}`
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          {!currentCalculator ? (
            <>
              <div style={styles.toolHeader}>
                <h2 style={styles.h2}>{t('toolTitle')}</h2>
                <p style={styles.summaryText}>{t('toolSubtitle')}</p>
              </div>

              <div style={styles.calculatorGrid}>
                {calculators.map((calc) => (
                  <div
                    key={calc.id}
                    style={styles.calculatorCard}
                    onClick={() => handleCalculatorSelect(calc.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.borderColor = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <span style={styles.cardIcon}>{calc.icon}</span>
                    <div style={styles.cardTitle}>{calc.title}</div>
                    <div style={styles.cardDescription}>{calc.description}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.formContainer}>
              <button
                style={styles.backBtn}
                onClick={() => setCurrentCalculator(null)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
              >
                ← {t('backButton')}
              </button>

              {currentCalculator === 'bmi' && (
                <>
                  <div style={styles.formHeader}>
                    <h3 style={styles.h2}>📊 {t('bmiTitle')}</h3>
                  </div>
                  <form onSubmit={handleBmiCalculate}>
                    <div style={styles.unitToggle}>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'metric' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('metric')}
                      >
                        {t('metric')}
                      </button>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'imperial' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('imperial')}
                      >
                        {t('imperial')}
                      </button>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('weight')} <span>({currentUnit === 'metric' ? 'kg' : 'lbs'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bmiInputs.weight}
                          onChange={(e) => setBmiInputs({...bmiInputs, weight: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('height')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bmiInputs.height}
                          onChange={(e) => setBmiInputs({...bmiInputs, height: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      style={styles.calculateButton}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {t('calculateBmi')}
                    </button>
                  </form>
                  {bmiResults && (
                    <div style={styles.results}>
                      <h3 style={styles.resultsTitle}>{t('yourResults')}</h3>
                      <div style={styles.resultGrid}>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('bmi')}</span>
                          <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>{bmiResults.bmi}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('category')}</span>
                          <span style={styles.resultValue}>{bmiResults.category}</span>
                        </div>
                      </div>
                      <div style={styles.interpretation}>
                        <div style={styles.interpretationTitle}>{t('whatThisMeans')}</div>
                        <div style={styles.interpretationText}>{bmiResults.interpretation}</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentCalculator === 'bmr' && (
                <>
                  <div style={styles.formHeader}>
                    <h3 style={styles.h2}>🔥 {t('bmrTitle')}</h3>
                  </div>
                  <form onSubmit={handleBmrCalculate}>
                    <div style={styles.unitToggle}>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'metric' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('metric')}
                      >
                        {t('metric')}
                      </button>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'imperial' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('imperial')}
                      >
                        {t('imperial')}
                      </button>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('age')}</label>
                        <input
                          type="number"
                          min="15"
                          max="120"
                          required
                          style={styles.input}
                          value={bmrInputs.age}
                          onChange={(e) => setBmrInputs({...bmrInputs, age: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('gender')}</label>
                        <select
                          required
                          style={styles.select}
                          value={bmrInputs.gender}
                          onChange={(e) => setBmrInputs({...bmrInputs, gender: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                          <option value="">{t('selectGender')}</option>
                          <option value="male">{t('male')}</option>
                          <option value="female">{t('female')}</option>
                        </select>
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('weight')} <span>({currentUnit === 'metric' ? 'kg' : 'lbs'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bmrInputs.weight}
                          onChange={(e) => setBmrInputs({...bmrInputs, weight: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('height')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bmrInputs.height}
                          onChange={(e) => setBmrInputs({...bmrInputs, height: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('activityLevel')}</label>
                      <select
                        required
                        style={styles.select}
                        value={bmrInputs.activity}
                        onChange={(e) => setBmrInputs({...bmrInputs, activity: e.target.value})}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                      >
                        <option value="">{t('selectActivity')}</option>
                        <option value="1.2">{t('sedentary')}</option>
                        <option value="1.375">{t('lightlyActive')}</option>
                        <option value="1.55">{t('moderatelyActive')}</option>
                        <option value="1.725">{t('veryActive')}</option>
                        <option value="1.9">{t('extraActive')}</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      style={styles.calculateButton}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {t('calculateBmr')}
                    </button>
                  </form>
                  {bmrResults && (
                    <div style={styles.results}>
                      <h3 style={styles.resultsTitle}>{t('yourResults')}</h3>
                      <div style={styles.resultGrid}>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('bmr')}</span>
                          <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>{bmrResults.bmr} {t('calPerDay')}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('dailyCalories')}</span>
                          <span style={styles.resultValue}>{bmrResults.tdee} {t('calPerDay')}</span>
                        </div>
                      </div>
                      <div style={styles.interpretation}>
                        <div style={styles.interpretationTitle}>{t('calorieGoals')}</div>
                        <div style={styles.interpretationText}>
                          <strong>{t('maintainWeight')}:</strong> {bmrResults.tdee} {t('caloriesPerDay')}<br />
                          <strong>{t('mildWeightLoss')}:</strong> {bmrResults.weightLoss} {t('caloriesPerDay')}<br />
                          <strong>{t('weightGain')}:</strong> {bmrResults.weightGain} {t('caloriesPerDay')}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentCalculator === 'bodyfat' && (
                <>
                  <div style={styles.formHeader}>
                    <h3 style={styles.h2}>📏 {t('bodyFatTitle')}</h3>
                  </div>
                  <form onSubmit={handleBodyFatCalculate}>
                    <div style={styles.unitToggle}>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'metric' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('metric')}
                      >
                        {t('metric')}
                      </button>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'imperial' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('imperial')}
                      >
                        {t('imperial')}
                      </button>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('age')}</label>
                        <input
                          type="number"
                          min="15"
                          max="120"
                          required
                          style={styles.input}
                          value={bfInputs.age}
                          onChange={(e) => setBfInputs({...bfInputs, age: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('gender')}</label>
                        <select
                          required
                          style={styles.select}
                          value={bfInputs.gender}
                          onChange={(e) => setBfInputs({...bfInputs, gender: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                          <option value="">{t('selectGender')}</option>
                          <option value="male">{t('male')}</option>
                          <option value="female">{t('female')}</option>
                        </select>
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('height')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bfInputs.height}
                          onChange={(e) => setBfInputs({...bfInputs, height: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('neck')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bfInputs.neck}
                          onChange={(e) => setBfInputs({...bfInputs, neck: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('waist')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={bfInputs.waist}
                          onChange={(e) => setBfInputs({...bfInputs, waist: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      {bfInputs.gender === 'female' && (
                        <div style={styles.formGroup}>
                          <label style={styles.label}>
                            {t('hip')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            required
                            style={styles.input}
                            value={bfInputs.hip}
                            onChange={(e) => setBfInputs({...bfInputs, hip: e.target.value})}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      style={styles.calculateButton}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {t('calculateBodyFat')}
                    </button>
                  </form>
                  {bfResults && (
                    <div style={styles.results}>
                      <h3 style={styles.resultsTitle}>{t('yourResults')}</h3>
                      <div style={styles.resultGrid}>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('bodyFatPercentage')}</span>
                          <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>{bfResults.bodyFat}%</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('category')}</span>
                          <span style={styles.resultValue}>{bfResults.category}</span>
                        </div>
                      </div>
                      <div style={styles.interpretation}>
                        <div style={styles.interpretationTitle}>{t('bodyFatClassification')}</div>
                        <div style={styles.interpretationText}>{bfResults.interpretation}</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentCalculator === 'calorie' && (
                <>
                  <div style={styles.formHeader}>
                    <h3 style={styles.h2}>🍎 {t('calorieTitle')}</h3>
                  </div>
                  <form onSubmit={handleCalorieCalculate}>
                    <div style={styles.unitToggle}>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'metric' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('metric')}
                      >
                        {t('metric')}
                      </button>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'imperial' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('imperial')}
                      >
                        {t('imperial')}
                      </button>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('age')}</label>
                        <input
                          type="number"
                          min="15"
                          max="120"
                          required
                          style={styles.input}
                          value={calInputs.age}
                          onChange={(e) => setCalInputs({...calInputs, age: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('gender')}</label>
                        <select
                          required
                          style={styles.select}
                          value={calInputs.gender}
                          onChange={(e) => setCalInputs({...calInputs, gender: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                          <option value="">{t('selectGender')}</option>
                          <option value="male">{t('male')}</option>
                          <option value="female">{t('female')}</option>
                        </select>
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('weight')} <span>({currentUnit === 'metric' ? 'kg' : 'lbs'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={calInputs.weight}
                          onChange={(e) => setCalInputs({...calInputs, weight: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('height')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={calInputs.height}
                          onChange={(e) => setCalInputs({...calInputs, height: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('activityLevel')}</label>
                      <select
                        required
                        style={styles.select}
                        value={calInputs.activity}
                        onChange={(e) => setCalInputs({...calInputs, activity: e.target.value})}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                      >
                        <option value="">{t('selectActivity')}</option>
                        <option value="1.2">{t('sedentary')}</option>
                        <option value="1.375">{t('lightlyActive')}</option>
                        <option value="1.55">{t('moderatelyActive')}</option>
                        <option value="1.725">{t('veryActive')}</option>
                        <option value="1.9">{t('extraActive')}</option>
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('goal')}</label>
                      <select
                        required
                        style={styles.select}
                        value={calInputs.goal}
                        onChange={(e) => setCalInputs({...calInputs, goal: e.target.value})}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                      >
                        <option value="">{t('selectGoal')}</option>
                        <option value="lose">{t('weightLoss')}</option>
                        <option value="maintain">{t('maintainWeight')}</option>
                        <option value="gain">{t('weightGain')}</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      style={styles.calculateButton}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {t('calculateCalories')}
                    </button>
                  </form>
                  {calResults && (
                    <div style={styles.results}>
                      <h3 style={styles.resultsTitle}>{t('yourCalorieGoals')}</h3>
                      <div style={styles.resultGrid}>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('maintenance')}</span>
                          <span style={styles.resultValue}>{calResults.maintain} {t('cal')}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('weightLoss')}</span>
                          <span style={styles.resultValue}>{calResults.loss} {t('cal')}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('weightGain')}</span>
                          <span style={styles.resultValue}>{calResults.gain} {t('cal')}</span>
                        </div>
                      </div>
                      <div style={styles.interpretation}>
                        <div style={styles.interpretationTitle}>{t('recommendedDailyIntake')}</div>
                        <div style={styles.interpretationText}>{calResults.interpretation}</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentCalculator === 'idealweight' && (
                <>
                  <div style={styles.formHeader}>
                    <h3 style={styles.h2}>⚖️ {t('idealWeightTitle')}</h3>
                  </div>
                  <form onSubmit={handleIdealWeightCalculate}>
                    <div style={styles.unitToggle}>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'metric' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('metric')}
                      >
                        {t('metric')}
                      </button>
                      <button
                        type="button"
                        style={{...styles.unitBtn, ...(currentUnit === 'imperial' ? styles.unitBtnActive : {})}}
                        onClick={() => setCurrentUnit('imperial')}
                      >
                        {t('imperial')}
                      </button>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('gender')}</label>
                        <select
                          required
                          style={styles.select}
                          value={iwInputs.gender}
                          onChange={(e) => setIwInputs({...iwInputs, gender: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                          <option value="">{t('selectGender')}</option>
                          <option value="male">{t('male')}</option>
                          <option value="female">{t('female')}</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          {t('height')} <span>({currentUnit === 'metric' ? 'cm' : 'inches'})</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          style={styles.input}
                          value={iwInputs.height}
                          onChange={(e) => setIwInputs({...iwInputs, height: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      style={styles.calculateButton}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {t('calculateIdealWeight')}
                    </button>
                  </form>
                  {iwResults && (
                    <div style={styles.results}>
                      <h3 style={styles.resultsTitle}>{t('yourIdealWeightRange')}</h3>
                      <div style={styles.resultGrid}>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('robinsonFormula')}</span>
                          <span style={styles.resultValue}>{iwResults.robinson} {currentUnit === 'metric' ? 'kg' : 'lbs'}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('millerFormula')}</span>
                          <span style={styles.resultValue}>{iwResults.miller} {currentUnit === 'metric' ? 'kg' : 'lbs'}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('devineFormula')}</span>
                          <span style={styles.resultValue}>{iwResults.devine} {currentUnit === 'metric' ? 'kg' : 'lbs'}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('hamwiFormula')}</span>
                          <span style={styles.resultValue}>{iwResults.hamwi} {currentUnit === 'metric' ? 'kg' : 'lbs'}</span>
                        </div>
                      </div>
                      <div style={styles.interpretation}>
                        <div style={styles.interpretationTitle}>{t('healthyWeightRange')}</div>
                        <div style={styles.interpretationText}>
                          {t('idealWeightInterpretation', {
                            min: iwResults.min,
                            max: iwResults.max,
                            avg: iwResults.avg,
                            unit: currentUnit === 'metric' ? 'kg' : 'lbs'
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentCalculator === 'heartrate' && (
                <>
                  <div style={styles.formHeader}>
                    <h3 style={styles.h2}>❤️ {t('heartRateTitle')}</h3>
                  </div>
                  <form onSubmit={handleHeartRateCalculate}>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('age')}</label>
                        <input
                          type="number"
                          min="15"
                          max="120"
                          required
                          style={styles.input}
                          value={hrInputs.age}
                          onChange={(e) => setHrInputs({...hrInputs, age: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t('restingHeartRate')}</label>
                        <input
                          type="number"
                          min="40"
                          max="120"
                          placeholder={t('optional')}
                          style={styles.input}
                          value={hrInputs.resting}
                          onChange={(e) => setHrInputs({...hrInputs, resting: e.target.value})}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      style={styles.calculateButton}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {t('calculateHeartRate')}
                    </button>
                  </form>
                  {hrResults && (
                    <div style={styles.results}>
                      <h3 style={styles.resultsTitle}>{t('yourHeartRateZones')}</h3>
                      <div style={styles.resultGrid}>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('maxHeartRate')}</span>
                          <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>{hrResults.maxHR} {t('bpm')}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('fatBurnZone')}</span>
                          <span style={styles.resultValue}>{hrResults.fatBurn} {t('bpm')}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('cardioZone')}</span>
                          <span style={styles.resultValue}>{hrResults.cardio} {t('bpm')}</span>
                        </div>
                        <div style={styles.resultItem}>
                          <span style={styles.resultLabel}>{t('peakZone')}</span>
                          <span style={styles.resultValue}>{hrResults.peak} {t('bpm')}</span>
                        </div>
                      </div>
                      <div style={styles.interpretation}>
                        <div style={styles.interpretationTitle}>{t('trainingZonesGuide')}</div>
                        <div style={styles.interpretationText}>
                          <strong>{t('fatBurnZone')}:</strong> {t('fatBurnDescription')}<br />
                          <strong>{t('cardioZone')}:</strong> {t('cardioDescription')}<br />
                          <strong>{t('peakZone')}:</strong> {t('peakDescription')}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insight1')}</p>
        <p style={styles.paragraph}>{t('insight2')}</p>
        <p style={styles.paragraph}>{t('insight3')}</p>
        <p style={styles.paragraph}>{t('insight4')}</p>
        <p style={styles.paragraph}>{t('insight5')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('bmiDetailsTitle')}</h3>
        <p style={styles.paragraph}>{t('bmiDetails')}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('bmrDetailsTitle')}</h3>
        <p style={styles.paragraph}>{t('bmrDetails')}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('bodyFatDetailsTitle')}</h3>
        <p style={styles.paragraph}>{t('bodyFatDetails')}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('calorieDetailsTitle')}</h3>
        <p style={styles.paragraph}>{t('calorieDetails')}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('idealWeightDetailsTitle')}</h3>
        <p style={styles.paragraph}>{t('idealWeightDetails')}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('heartRateDetailsTitle')}</h3>
        <p style={styles.paragraph}>{t('heartRateDetails')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a
            href="https://www.garagegymreviews.com/best-fitness-trackers"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference1')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.livescience.com/best-fitness-tracker"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference2')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://blog.ochsner.org/articles/walking-to-lose-weight-calculators/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference3')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.techgearlab.com/topics/health-fitness/best-fitness-tracker"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference4')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.fullestop.com/blog/fitness-app-development-features-trends-and-costs"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference5')}
          </a>
        </div>
      </section>
    </div>
  );
}